import { Document, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import {
  clean,
  Footer,
  Header,
  INK,
  LINE,
  MUTED,
  NAVY,
  PAGE_STYLE,
  StatRow,
  TEAL,
  TINT,
} from './pdfKit'
import {
  BLOCK_COLORS,
  BLOCK_LABELS,
} from '../../pages/fleetEquipment/vehicles/vehicleAvailabilityConstants'

// Printed version of the vehicle availability week view: one row per vehicle,
// one column per day, each day cell stacking the PTS duties, event
// deployments and unavailability that touch it.
const DOC_LABEL = 'Vehicle Availability'

// A multi-day block's times are clamped to this day, so a Tuesday cell shows
// the hours it occupies on Tuesday - or "All day" when it spans the whole day.
const dayWindowText = (block, dayStart, dayEnd) => {
  const start = moment.max(moment(block.start), dayStart)
  const end = moment.min(moment(block.end), dayEnd)
  if (start.isSame(dayStart) && end.isSame(dayEnd)) return 'All day'
  return `${start.format('HH:mm')} - ${end.format('HH:mm')}`
}

const DayCell = ({ dayStart, dayEnd, blocks }) => {
  const touching = blocks.filter(
    (block) =>
      moment(block.start).isBefore(dayEnd) && moment(block.end).isAfter(dayStart),
  )
  return (
    <View style={{ flex: 1, paddingHorizontal: 2 }}>
      {touching.length === 0 ? (
        <Text style={{ fontSize: 6.5, color: LINE }}>-</Text>
      ) : (
        touching.map((block) => (
          <View
            key={block._id}
            style={{
              backgroundColor: BLOCK_COLORS[block.type] || MUTED,
              borderRadius: 2,
              paddingVertical: 1.5,
              paddingHorizontal: 3,
              marginBottom: 1.5,
            }}
          >
            <Text style={{ fontSize: 5.5, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>
              {clean(block.label || BLOCK_LABELS[block.type] || block.type)}
            </Text>
            <Text style={{ fontSize: 5, color: '#FFFFFF' }}>
              {dayWindowText(block, dayStart, dayEnd)}
            </Text>
          </View>
        ))
      )}
    </View>
  )
}

const VehicleWeekSchedulePDF = ({
  vehicles,
  blocks,
  weekStart,
  orgName,
  logo,
  generatedBy,
}) => {
  const generatedAt = new Date()
  const start = moment(weekStart)
  const days = Array.from({ length: 7 }, (_, i) => start.clone().add(i, 'days'))
  const title = `Week commencing ${start.format('D MMM YYYY')}`

  const byVehicle = vehicles.map((vehicle) => ({
    vehicle,
    blocks: blocks.filter((block) => block.vehicleId === vehicle._id),
  }))

  const stats = [
    { label: 'Vehicles', value: vehicles.length },
    {
      label: 'Commitments',
      value: blocks.filter((b) => b.type !== 'unavailable').length,
      sub: 'PTS duties and event deployments',
    },
    {
      label: 'Unavailability',
      value: blocks.filter((b) => b.type === 'unavailable').length,
      color: blocks.some((b) => b.type === 'unavailable') ? '#B42318' : undefined,
    },
  ]

  return (
    <Document
      title={`${DOC_LABEL} - ${title}`}
      author={orgName}
      subject={DOC_LABEL}
      creator="AmbuNet"
      options={{ isEvalSupported: false }}
    >
      <Page size="A4" orientation="landscape" style={PAGE_STYLE}>
        <Header orgName={orgName} logo={logo} docLabel={DOC_LABEL} title={title} />

        <View style={{ marginBottom: 8 }}>
          <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: NAVY }}>
            Vehicle Availability
          </Text>
          <View style={{ height: 2, backgroundColor: TEAL, width: 40, marginTop: 5 }} />
          <Text style={{ fontSize: 7.5, color: MUTED, marginTop: 6 }}>
            {clean(
              `Everything each vehicle is committed to for the week commencing ${start.format('dddd D MMMM YYYY')}.`,
            )}
          </Text>
        </View>

        <StatRow items={stats} />

        <View style={{ flexDirection: 'row', marginBottom: 8 }}>
          {Object.entries(BLOCK_LABELS).map(([type, label]) => (
            <View
              key={type}
              style={{ flexDirection: 'row', alignItems: 'center', marginRight: 10 }}
            >
              <View
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: BLOCK_COLORS[type],
                  marginRight: 3,
                }}
              />
              <Text style={{ fontSize: 6.5, color: MUTED }}>{label}</Text>
            </View>
          ))}
        </View>

        <View
          style={{
            flexDirection: 'row',
            backgroundColor: NAVY,
            paddingVertical: 4,
            paddingHorizontal: 3,
            borderRadius: 3,
          }}
          fixed
          wrap={false}
        >
          <Text
            style={{
              flex: 1.6,
              paddingHorizontal: 3,
              fontSize: 6,
              color: '#FFFFFF',
              fontFamily: 'Helvetica-Bold',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Vehicle
          </Text>
          {days.map((day) => (
            <Text
              key={day.format('YYYY-MM-DD')}
              style={{
                flex: 1,
                paddingHorizontal: 3,
                fontSize: 6,
                color: '#FFFFFF',
                fontFamily: 'Helvetica-Bold',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              {day.format('ddd D MMM')}
            </Text>
          ))}
        </View>

        {byVehicle.length === 0 ? (
          <Text style={{ fontSize: 7, color: MUTED, padding: 5 }}>No vehicles to print</Text>
        ) : (
          byVehicle.map(({ vehicle, blocks: vehicleBlocks }, r) => (
            <View
              key={vehicle._id}
              style={{
                flexDirection: 'row',
                paddingVertical: 4,
                paddingHorizontal: 3,
                backgroundColor: r % 2 ? TINT : undefined,
                borderBottomWidth: 0.5,
                borderBottomColor: LINE,
              }}
              wrap={false}
            >
              <View style={{ flex: 1.6, paddingHorizontal: 3 }}>
                <Text style={{ fontSize: 7, color: INK, fontFamily: 'Helvetica-Bold' }}>
                  {clean(vehicle.callSign || vehicle.regPlate)}
                </Text>
                <Text style={{ fontSize: 6, color: MUTED, marginTop: 1 }}>
                  {clean(
                    [vehicle.regPlate, vehicle.vehicleType || 'Vehicle']
                      .filter(Boolean)
                      .join(' - '),
                  )}
                </Text>
                {vehicle.status && vehicle.status !== 'Operational' ? (
                  <Text style={{ fontSize: 6, color: '#B42318', marginTop: 1 }}>
                    {clean(vehicle.status)}
                  </Text>
                ) : null}
              </View>
              {days.map((day) => (
                <DayCell
                  key={day.format('YYYY-MM-DD')}
                  dayStart={day.clone().startOf('day')}
                  dayEnd={day.clone().endOf('day')}
                  blocks={vehicleBlocks}
                />
              ))}
            </View>
          ))
        )}

        <Footer
          orgName={orgName}
          docLabel={DOC_LABEL}
          title={title}
          generatedAt={generatedAt}
          generatedBy={generatedBy}
        />
      </Page>
    </Document>
  )
}

export default VehicleWeekSchedulePDF
